const STYLE_ID = 'rift-debug-report-style';

const escapeText = (value) => String(value ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').trim();
const coord = (n) => Number.isFinite(n) ? n.toFixed(2) : '—';

export class DebugReport {
  constructor(host, ctx) {
    this.host = host;
    this.ctx = ctx;
    this.visible = false;
    this.screenshot = null;
    this.positions = [];
    this._savedScale = 1;
    this._savedInputEnabled = true;
    this._savedControl = true;
    this._build();
    this._onKey = (event) => {
      if (!this.visible) return;
      if (event.code === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      }
    };
    document.addEventListener('keydown', this._onKey, true);
  }

  _build() {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }
    this.root = document.createElement('section');
    this.root.className = 'dr-root';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Debug report');
    this.root.innerHTML = `
      <div class="dr-shell">
        <header class="dr-head"><div><span>FIELD REPORT / DEBUG</span><h1>REPORT A PROBLEM</h1></div><button class="dr-close" type="button" aria-label="Close">×</button></header>
        <div class="dr-grid">
          <div class="dr-shot"><img alt="Screenshot of the current game frame"><div class="dr-capture">CAPTURING CURRENT FRAME…</div></div>
          <aside><h2>PLAYER COORDINATES</h2><div class="dr-coords"></div></aside>
        </div>
        <form class="dr-form">
          <label>Title<input name="title" maxlength="100" minlength="3" required placeholder="Short summary"></label>
          <label>Description<textarea name="description" maxlength="4000" minlength="10" required placeholder="What happened, and what did you expect?"></textarea></label>
          <div class="dr-actions"><span class="dr-status" role="status"></span><button class="dr-cancel" type="button">CANCEL</button><button class="dr-submit" type="submit">SEND TO BACKLOG</button></div>
        </form>
      </div>`;
    this.host.appendChild(this.root);
    this.image = this.root.querySelector('img');
    this.captureLabel = this.root.querySelector('.dr-capture');
    this.coords = this.root.querySelector('.dr-coords');
    this.form = this.root.querySelector('form');
    this.status = this.root.querySelector('.dr-status');
    this.submit = this.root.querySelector('.dr-submit');
    this.root.querySelector('.dr-close').addEventListener('click', () => this.close());
    this.root.querySelector('.dr-cancel').addEventListener('click', () => this.close());
    this.form.addEventListener('submit', (event) => this._submit(event));
  }

  async open() {
    if (this.visible) return;
    this.visible = true;
    this.screenshot = null;
    this.positions = this._collectPositions();
    this._renderPositions();
    this.form.reset();
    this.status.textContent = '';
    this.submit.disabled = true;
    this.image.removeAttribute('src');
    this.captureLabel.textContent = 'CAPTURING CURRENT FRAME…';
    this.captureLabel.hidden = false;
    this.root.hidden = false;
    this._savedScale = this.ctx.time.scale;
    this._savedInputEnabled = this.ctx.input.enabled;
    this._savedControl = this.ctx.peek('player')?.controlEnabled !== false;
    this.ctx.time.scale = 0;
    this.ctx.input.enabled = false;
    this.ctx.peek('player')?.setControlEnabled?.(false);
    try { document.exitPointerLock?.(); } catch { /* best effort */ }

    try {
      this.screenshot = await this.ctx.get('render').captureFrame();
      if (!this.visible) return;
      this.image.src = this.screenshot.dataUrl;
      this.captureLabel.hidden = true;
      this.submit.disabled = false;
      this.form.elements.title.focus({ preventScroll: true });
    } catch (err) {
      console.error('[debug-report] screenshot failed', err);
      if (!this.visible) return;
      this.captureLabel.textContent = 'SCREENSHOT UNAVAILABLE';
      this.submit.disabled = false;
    }
  }

  close() {
    if (!this.visible) return;
    this.visible = false;
    this.root.hidden = true;
    this.ctx.time.scale = this._savedScale;
    this.ctx.input.enabled = this._savedInputEnabled;
    this.ctx.peek('player')?.setControlEnabled?.(this._savedControl);
    this.screenshot = null;
  }

  _collectPositions() {
    const rows = [];
    const player = this.ctx.peek('player');
    if (player?.position) rows.push({ name: 'YOU', kind: 'local', p: player.position });
    const net = this.ctx.peek('net');
    for (const remote of net?.players?.values?.() ?? []) {
      if (remote?.position) rows.push({ name: escapeText(remote.name) || `PLAYER ${remote.id}`, kind: 'online', p: remote.position });
    }
    const ai = this.ctx.peek('ai');
    const actors = typeof ai?.getHudActors === 'function' ? ai.getHudActors() : ai?.agents;
    if (Array.isArray(actors)) {
      let index = 0;
      for (const actor of actors) {
        const p = actor?.position ?? actor?.pos;
        if (!p || actor?.remote || actor?.alive === false || actor?.dead) continue;
        rows.push({ name: escapeText(actor.name) || `AI ${++index}`, kind: 'offline', p });
      }
    }
    return rows.slice(0, 32).map(({ name, kind, p }) => ({ name, kind, x: p.x, y: p.y, z: p.z }));
  }

  _renderPositions() {
    this.coords.textContent = '';
    if (!this.positions.length) {
      this.coords.textContent = 'No active players found.';
      return;
    }
    for (const row of this.positions) {
      const item = document.createElement('div');
      item.className = 'dr-coordinate';
      const name = document.createElement('strong');
      name.textContent = row.name;
      const values = document.createElement('code');
      values.textContent = `X ${coord(row.x)}  Y ${coord(row.y)}  Z ${coord(row.z)}`;
      item.append(name, values);
      this.coords.appendChild(item);
    }
  }

  async _submit(event) {
    event.preventDefault();
    if (this.submit.disabled) return;
    const data = new FormData(this.form);
    const title = String(data.get('title') ?? '').trim();
    const description = String(data.get('description') ?? '').trim();
    if (title.length < 3 || description.length < 10) {
      this.status.textContent = 'ADD A TITLE AND A LITTLE MORE DETAIL.';
      return;
    }
    this.submit.disabled = true;
    this.status.textContent = 'SENDING REPORT…';
    try {
      const response = await fetch('/mp/bug-reports', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          title,
          description,
          screenshot: this.screenshot?.dataUrl ?? null,
          positions: this.positions,
          page_url: location.href,
          user_agent: navigator.userAgent,
          viewport: `${innerWidth}x${innerHeight}@${devicePixelRatio || 1}`,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.ok) throw new Error(body.error || 'The report service is unavailable.');
      this.status.textContent = body.screenshotAttached === false && this.screenshot
        ? 'SENT; SCREENSHOT COULD NOT BE ATTACHED.'
        : 'SENT TO BACKLOG.';
      this.submit.textContent = 'SENT';
      setTimeout(() => {
        this.submit.textContent = 'SEND TO BACKLOG';
        this.close();
      }, 900);
    } catch (err) {
      this.status.textContent = String(err?.message || 'Could not send report.').toUpperCase();
      this.submit.disabled = false;
    }
  }

  dispose() {
    document.removeEventListener('keydown', this._onKey, true);
    this.root.remove();
  }
}

const CSS = `
.dr-root{position:fixed;inset:0;z-index:100;display:grid;place-items:center;padding:clamp(12px,3vw,42px);overflow:auto;background:rgba(3,6,8,.94);color:#dce9ee;font:12px/1.45 ui-sans-serif,system-ui,sans-serif}
.dr-root[hidden]{display:none!important}.dr-shell{width:min(1120px,100%);padding:clamp(18px,3vw,36px);border:1px solid rgba(118,177,151,.38);background:linear-gradient(145deg,#0d1516,#080d10);box-shadow:0 30px 90px #000}
.dr-head{display:flex;align-items:flex-start;justify-content:space-between;border-bottom:1px solid rgba(150,190,175,.18);padding-bottom:16px}.dr-head span,.dr-grid h2{color:#76b197;font:700 10px/1 ui-monospace,monospace;letter-spacing:.22em}.dr-head h1{margin:8px 0 0;font:800 clamp(25px,4vw,44px)/.95 ui-sans-serif,system-ui,sans-serif;letter-spacing:.05em}.dr-close{border:0;background:none;color:#91a5ac;font-size:28px;cursor:pointer}.dr-grid{display:grid;grid-template-columns:minmax(0,2fr) minmax(230px,1fr);gap:18px;margin:20px 0}.dr-shot{position:relative;min-height:220px;aspect-ratio:16/9;display:grid;place-items:center;overflow:hidden;border:1px solid rgba(150,190,175,.2);background:#020405}.dr-shot img{width:100%;height:100%;object-fit:cover}.dr-capture{position:absolute;color:#76b197;font:700 10px/1 ui-monospace,monospace;letter-spacing:.16em}.dr-grid aside{min-height:0;border:1px solid rgba(150,190,175,.16);padding:16px;background:rgba(255,255,255,.018)}.dr-grid h2{margin:0 0 12px}.dr-coords{max-height:260px;overflow:auto}.dr-coordinate{display:grid;gap:3px;padding:8px 0;border-bottom:1px solid rgba(150,190,175,.1)}.dr-coordinate strong{font-size:11px;letter-spacing:.08em}.dr-coordinate code{color:#8aa0a7;font-size:10px}.dr-form{display:grid;gap:12px}.dr-form label{display:grid;gap:6px;color:#8fa5ac;font:700 10px/1 ui-monospace,monospace;letter-spacing:.16em;text-transform:uppercase}.dr-form input,.dr-form textarea{box-sizing:border-box;width:100%;border:1px solid rgba(150,190,175,.25);background:#070b0d;color:#eef7f4;padding:11px 12px;font:13px/1.45 ui-sans-serif,system-ui,sans-serif;outline:none}.dr-form textarea{min-height:100px;resize:vertical}.dr-form input:focus,.dr-form textarea:focus{border-color:#76b197;box-shadow:0 0 0 1px rgba(118,177,151,.3)}.dr-actions{display:flex;align-items:center;gap:10px;margin-top:4px}.dr-status{margin-right:auto;color:#9bb4aa;font:700 10px/1.3 ui-monospace,monospace;letter-spacing:.1em}.dr-actions button{padding:11px 17px;border:1px solid rgba(150,190,175,.28);background:transparent;color:#b5c6ca;font:700 10px/1 ui-monospace,monospace;letter-spacing:.15em;cursor:pointer}.dr-submit{background:#76b197!important;color:#07100c!important;border-color:#76b197!important}.dr-submit:disabled{opacity:.45;cursor:wait}@media(max-width:720px){.dr-grid{grid-template-columns:1fr}.dr-grid aside{max-height:180px}.dr-actions{flex-wrap:wrap}.dr-status{flex-basis:100%}}
`;
